/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { jsx } from '@emotion/react';

import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useMutation } from '@apollo/client';
import { compose } from 'recompose';

import Retracked from 'js/app/retracked';
import connectToRouter from 'js/lib/connectToRouter';
import user from 'js/lib/user';

import type { PortfolioBuilderFormRef } from 'bundles/portfolio-projects/components/PortfolioBuilderForm';
import PortfolioBuilderForm from 'bundles/portfolio-projects/components/PortfolioBuilderForm';
import PortfolioProjectHideDialog from 'bundles/portfolio-projects/components/PortfolioProjectHideDialog';
import PortfolioProjectPublishDialog from 'bundles/portfolio-projects/components/PortfolioProjectPublishDialog';
import PortfolioProjectPublishSuccessDialog from 'bundles/portfolio-projects/components/PortfolioProjectPublishSuccessDialog';
import hidePortfolioProjectMutation from 'bundles/portfolio-projects/queries/HidePortfolioProjectMutation.graphql';
import publishPortfolioProjectMutation from 'bundles/portfolio-projects/queries/PublishPortfolioProjectMutation.graphql';
import savePortfolioProjectMutation from 'bundles/portfolio-projects/queries/SavePortfolioProjectMutation.graphql';
import type {
  UserPortfolioProjectHideMutation,
  UserPortfolioProjectHideMutationVariables,
} from 'bundles/portfolio-projects/queries/__generated__/HidePortfolioProjectMutation';
import type {
  UserPortfolioProjectPublishMutation,
  UserPortfolioProjectPublishMutationVariables,
} from 'bundles/portfolio-projects/queries/__generated__/PublishPortfolioProjectMutation';
import type {
  UserPortfolioProject_SaveMutation as SavePortfolioProjectMutation,
  UserPortfolioProject_SaveMutationVariables as SavePortfolioProjectMutationVariables,
} from 'bundles/portfolio-projects/queries/__generated__/SavePortfolioProjectMutation';
import type { SharedPortfolioProject_UserPortfolioProjectFragment as UserPortfolioProject } from 'bundles/portfolio-projects/queries/__generated__/SharedPortfolioProject_UserPortfolioProject';
import type { Nullable } from 'bundles/portfolio-projects/types';
import { safeConvertToCmlContentInput } from 'bundles/portfolio-projects/utils/cmlHelpers';

export type PortfolioBuilderTrigger = 'course_shell' | 'learner_profile' | 'unknown';

type TrackedData = {
  productId?: string;
};

const Wrapper: React.LegacyFunctionComponentWithChildren = ({ children }) => {
  return <>{children}</>;
};

export const TrackedWrapper = Retracked.createTrackedContainer<React.PropsWithChildren<TrackedData>>(
  ({ productId }) => ({
    // eslint-disable-next-line camelcase
    product_id: productId,
  })
)(Wrapper);

export type PortfolioBuilderProps = {
  title: string;
  canOpenProjectHome: boolean;
  project: Nullable<UserPortfolioProject>;
  projectsList?: UserPortfolioProject[];
  open: boolean;
  onClose: () => void;
};

export type PropsFromCaller = PortfolioBuilderProps & {
  onProjectUpdate?: (project: UserPortfolioProject) => void;
};

type TrackedPortfolioBuilderProps = PropsFromCaller & {
  trigger: PortfolioBuilderTrigger;
};

const PortfolioBuilder = ({
  open,
  project,
  projectsList = [],
  canOpenProjectHome,
  onProjectUpdate,
  onClose,
  ...rest
}: PropsFromCaller) => {
  const formRef = useRef<PortfolioBuilderFormRef>(null);
  const [currentProject, setCurrentProject] = useState<Nullable<UserPortfolioProject>>(null);
  const [isHideDialogOpen, setIsHideDialogOpen] = useState<boolean>(false);
  const [isPublishDialogOpen, setIsPublishDialogOpen] = useState<boolean>(false);
  const [isPublishSuccessDialogOpen, setIsPublishSuccessDialogOpen] = useState<boolean>(false);

  const closeHideDialog = () => setIsHideDialogOpen(false);
  const closePublishDialog = () => setIsPublishDialogOpen(false);
  const closeBuilderDialog = useCallback<(...args: $TSFixMe[]) => $TSFixMe>(() => {
    onClose();
    setCurrentProject(null);
  }, [onClose]);
  const closePublishSuccessDialog = useCallback<(...args: $TSFixMe[]) => $TSFixMe>(() => {
    setIsPublishSuccessDialogOpen(false);
    closeBuilderDialog();
  }, [closeBuilderDialog]);
  const onPublish = () => {
    setIsPublishDialogOpen(true);
  };
  const onUpdateProject = useCallback<(...args: $TSFixMe[]) => $TSFixMe>(
    (updatedProject: UserPortfolioProject) => {
      onProjectUpdate?.(updatedProject);
      setCurrentProject(updatedProject);
    },
    [onProjectUpdate]
  );

  const [saveChanges, { loading: saving, error: saveError, reset: resetSaveState }] = useMutation<
    SavePortfolioProjectMutation,
    SavePortfolioProjectMutationVariables
  >(savePortfolioProjectMutation, {
    onCompleted: (data: SavePortfolioProjectMutation) => {
      const response = data?.UserPortfolioProject_Save;
      if (response?.__typename === 'UserPortfolioProject_SaveResultSuccess') {
        const updatedProject = response.project;
        if (updatedProject) {
          onUpdateProject(updatedProject);
        }
      }
    },
    onError: () => {
      /** Errors will be handled by saveError variable and passed to the child component, but we should log it in Sentry when we have it configured */
      closePublishDialog();
    },
  });
  const [publish, { loading: publishing, error: publishError, reset: resetPublishState }] = useMutation<
    UserPortfolioProjectPublishMutation,
    UserPortfolioProjectPublishMutationVariables
  >(publishPortfolioProjectMutation, {
    onCompleted: (data: UserPortfolioProjectPublishMutation) => {
      const response = data?.UserPortfolioProject_Publish;
      if (response?.__typename === 'UserPortfolioProject_PublishResultSuccess') {
        const updatedProject = response.project;
        if (updatedProject) {
          onUpdateProject(updatedProject);
          closePublishDialog();
          setIsPublishSuccessDialogOpen(true);
        }
      }
    },
    onError: () => {
      /** Errors will be handled by publishError variable and passed to the child component, but we should log it in Sentry when we have it configured */
      closePublishDialog();
    },
  });
  const [hide, { loading: hiding, error: hideError, reset: resetHideState }] = useMutation<
    UserPortfolioProjectHideMutation,
    UserPortfolioProjectHideMutationVariables
  >(hidePortfolioProjectMutation, {
    onCompleted: (data: UserPortfolioProjectHideMutation) => {
      const response = data?.UserPortfolioProject_Hide;
      if (response?.__typename === 'UserPortfolioProject_HideResultSuccess') {
        const updatedProject = response.project;
        if (updatedProject) {
          onUpdateProject(updatedProject);
          closeHideDialog();
          closeBuilderDialog();
        }
      }
    },
    onError: () => {
      /** Errors will be handled by hideError variable and passed to the child component, but we should log it in Sentry when we have it configured */
      closeHideDialog();
    },
  });

  const hasFormError = !!saveError || !!publishError || !!hideError;

  const saveProject = useCallback<(...args: $TSFixMe[]) => $TSFixMe>(() => {
    const formValues = formRef.current?.getValues();

    if (!currentProject || !formValues) {
      return Promise.reject();
    }

    const { linkToWork, summary, solution, approach, artifacts } = formValues;

    return saveChanges({
      variables: {
        input: {
          projectId: currentProject.project.id,
          userId: `${user.get().id}`,
          linkToWork: linkToWork || null,
          solution: safeConvertToCmlContentInput(solution),
          approach: safeConvertToCmlContentInput(approach),
          summary,
          artifacts,
        },
      },
      context: {
        clientName: 'gatewayGql',
      },
    });
  }, [saveChanges, currentProject]);

  const publishProject = useCallback<(...args: $TSFixMe[]) => $TSFixMe>(() => {
    if (!currentProject) {
      return;
    }

    publish({
      variables: {
        input: {
          projectId: currentProject.project.id,
          userId: `${user.get().id}`,
        },
      },
      context: {
        clientName: 'gatewayGql',
      },
    });
  }, [publish, currentProject]);

  const hideProject = useCallback<(...args: $TSFixMe[]) => $TSFixMe>(() => {
    if (!currentProject?.isPublished) {
      return;
    }

    hide({
      variables: {
        input: {
          projectId: currentProject.project.id,
          userId: `${user.get().id}`,
        },
      },
      context: {
        clientName: 'gatewayGql',
      },
    });
  }, [hide, currentProject]);

  const onPublishProject = useCallback<(...args: $TSFixMe[]) => $TSFixMe>(async () => {
    const response = await saveProject();

    if (response.data?.UserPortfolioProject_Save?.__typename === 'UserPortfolioProject_SaveResultSuccess') {
      await publishProject();
    }
  }, [saveProject, publishProject]);

  useEffect(() => {
    if (!open && hasFormError) {
      resetSaveState();
      resetPublishState();
      resetHideState();
    }
  }, [open, hasFormError, resetSaveState, resetPublishState, resetHideState]);

  // Auto populate the first project in the list when adding a new project
  useEffect(() => {
    if (currentProject) {
      return;
    }

    const currentPortfolioProject = !project && projectsList.length > 0 ? projectsList[0] : project;

    if (open && currentPortfolioProject) {
      setCurrentProject(currentPortfolioProject);
    }
  }, [open, project, projectsList, currentProject]);

  return (
    <TrackedWrapper productId={currentProject?.project.id}>
      <PortfolioBuilderForm
        ref={formRef}
        open={open}
        project={currentProject}
        saving={!isPublishDialogOpen ? saving : false}
        canOpenProjectHome={canOpenProjectHome}
        hasFormError={hasFormError}
        onClose={closeBuilderDialog}
        onSave={saveProject}
        onPublish={onPublish}
        onSelectProject={(proj: UserPortfolioProject) => setCurrentProject(proj)}
        onHide={() => setIsHideDialogOpen(true)}
        {...(projectsList.length > 1 && { projectsList })}
        {...rest}
      />
      <PortfolioProjectHideDialog
        open={isHideDialogOpen}
        loading={hiding}
        onClose={closeHideDialog}
        onHide={hideProject}
      />
      <PortfolioProjectPublishDialog
        open={isPublishDialogOpen}
        loading={saving || publishing}
        onClose={closePublishDialog}
        onPublish={onPublishProject}
      />
      <PortfolioProjectPublishSuccessDialog
        open={isPublishSuccessDialogOpen}
        canViewProject={!canOpenProjectHome}
        onClose={closePublishSuccessDialog}
      />
    </TrackedWrapper>
  );
};

const TrackedPortfolioBuilder = Retracked.createTrackedContainer<TrackedPortfolioBuilderProps>(({ trigger }) => ({
  namespace: {
    app: 'portfolio_projects',
    page: 'portfolio_builder',
  },
  trigger,
  // eslint-disable-next-line camelcase
  user_id: user?.get()?.id,
}))(PortfolioBuilder);

export default compose<TrackedPortfolioBuilderProps, PropsFromCaller>(
  connectToRouter((router) => {
    const { pathname } = router.location;
    let trigger: PortfolioBuilderTrigger = 'unknown';
    if (pathname.startsWith('/learn/')) {
      trigger = 'course_shell';
    } else if (pathname === '/account-profile') {
      trigger = 'learner_profile';
    }

    return { trigger };
  })
)(TrackedPortfolioBuilder);
